import React, { Fragment, useEffect, useState } from 'react';
import {
    Row, Col, Typography, Table, Button, Input, DatePicker,
    Flex, Space, Popconfirm, Tag, Modal, Form,
} from 'antd';
import { SearchOutlined, DownloadOutlined, DeleteOutlined, FilePdfOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import axios from '../../axios/interceptor';
import openNotification from '../../components/notification';

import { getSessions, deleteSession, getSettings } from './redux/slice';

const roundUpSeconds = (seconds, minutes) => {
    if (!minutes) return seconds;
    const m = minutes * 60;
    return Math.ceil(seconds / m) * m;
};

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const pad = n => String(n).padStart(2, '0');
const fmtSeconds = s => {
    if (!s) return '00:00:00';
    return `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
};

const openPdf = async (id) => {
    try {
        const res = await axios.get(`/support-session/export-pdf?id=${id}`, { responseType: 'blob' });
        const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        window.open(url, '_blank');
    } catch (e) {
        openNotification({ message: 'PDF konnte nicht geladen werden.', type: 'error' });
    }
};

const downloadCsv = (csv, filename) => {
    const bom = '\uFEFF';
    const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
};

const SupportVerlaufPage = () => {
    const dispatch = useDispatch();
    const { auth, supportTracking } = useSelector(s => s);
    const { t } = useTranslation();

    const { sessions, totalRecords, loading, settings } = supportTracking;
    const organizer = auth?.decodeToken?.organizer;

    const [search, setSearch] = useState('');
    const [dateRange, setDateRange] = useState([null, null]);
    const [page, setPage] = useState(1);
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [exportType, setExportType] = useState('billable');
    const [exportRange, setExportRange] = useState([null, null]);
    const [exportLoading, setExportLoading] = useState(false);

    const loadData = (p = page) => {
        if (!organizer) return;
        dispatch(getSessions({
            organizer,
            search,
            startDate: dateRange[0] ? dayjs(dateRange[0]).format('YYYY-MM-DD') : '',
            endDate: dateRange[1] ? dayjs(dateRange[1]).format('YYYY-MM-DD') : '',
            page: p,
            limit: 20,
        }));
    };

    useEffect(() => {
        loadData(1);
        if (organizer) dispatch(getSettings(organizer));
    }, [organizer]);

    const handleSearch = () => { setPage(1); loadData(1); };

    const handleDelete = (id) => {
        dispatch(deleteSession(id));
        setTimeout(() => loadData(page), 400);
    };

    const handleExport = async () => {
        setExportLoading(true);
        try {
            const endpoint = exportType === 'billable' ? '/support-session/export-csv' : '/support-session/export-activity-sheet';
            const res = await axios.post(endpoint, {
                organizer,
                startDate: exportRange[0] ? dayjs(exportRange[0]).format('YYYY-MM-DD') : '',
                endDate: exportRange[1] ? dayjs(exportRange[1]).format('YYYY-MM-DD') : '',
            });
            if (res.data.isSuccess && res.data.csv) {
                const filename = exportType === 'billable' ? 'support_berechenbar.csv' : 'support_taetigkeitsblatt.csv';
                downloadCsv(res.data.csv, filename);
                setExportModalOpen(false);
            } else {
                openNotification({ message: 'Keine Daten gefunden.', type: 'warning' });
            }
        } catch (e) {
            openNotification({ message: e.message, type: 'error' });
        } finally {
            setExportLoading(false);
        }
    };

    const columns = [
        {
            title: t('support_kundenId'),
            dataIndex: 'kundenId',
            key: 'kundenId',
            width: 130,
        },
        {
            title: t('support_kundenName'),
            dataIndex: 'kundenName',
            key: 'kundenName',
        },
        {
            title: t('support_ansprechpartner'),
            dataIndex: 'ansprechpartner',
            key: 'ansprechpartner',
        },
        {
            title: t('support_datum'),
            key: 'startTime',
            render: (_, r) => r.startTime ? dayjs(r.startTime).format('DD.MM.YYYY HH:mm') : '–',
            width: 150,
        },
        {
            title: t('support_dauer'),
            key: 'duration',
            render: (_, r) => <Text style={{ fontFamily: 'monospace' }}>{fmtSeconds(roundUpSeconds(r.durationSeconds, settings?.roundingMinutes))}</Text>,
            width: 110,
        },
        {
            title: t('support_berechenbar'),
            key: 'berechenbar',
            width: 110,
            render: (_, r) => (
                <Tag color={r.berechenbar ? 'blue' : 'default'}>
                    {r.berechenbar ? 'Ja' : 'Nein'}
                </Tag>
            ),
        },
        {
            title: t('support_taetigkeiten'),
            key: 'taetigkeiten',
            render: (_, r) => (
                <Flex gap={4} wrap>
                    {(r.taetigkeiten || []).map((t, i) => (
                        <Tag key={i}>
                            {t.taetigkeitName || t.freitext || '–'}
                        </Tag>
                    ))}
                </Flex>
            ),
        },
        {
            title: '',
            key: 'actions',
            width: 90,
            render: (_, r) => (
                <Space>
                    <Button
                        icon={<FilePdfOutlined />}
                        size='small'
                        onClick={() => openPdf(r._id)}
                        title='PDF anzeigen'
                    />
                    <Popconfirm
                        title={t('support_delete_confirm')}
                        onConfirm={() => handleDelete(r._id)}
                        okText={t('organizer_common_yes') || 'Ja'}
                        cancelText={t('organizer_common_cancelButton')}
                    >
                        <Button icon={<DeleteOutlined />} size='small' danger />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Fragment>
            <Flex vertical gap={16}>
                <Row justify='space-between' align='middle' className='padding-1 rounded-1' gutter={[16, 16]}>
                    <Col>
                        <Title level={4} type='success'>{t('support_verlauf_title')}</Title>
                    </Col>
                    <Col>
                        <Button icon={<DownloadOutlined />} onClick={() => setExportModalOpen(true)}>
                            {t('support_export')}
                        </Button>
                    </Col>
                </Row>

                <Row gutter={[8, 8]} align='middle'>
                    <Col xs={24} sm={10} md={8}>
                        <Input
                            placeholder={t('organizer_common_search')}
                            prefix={<SearchOutlined />}
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            onPressEnter={handleSearch}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={10} md={8}>
                        <RangePicker
                            format='DD.MM.YYYY'
                            style={{ width: '100%' }}
                            value={dateRange}
                            onChange={v => setDateRange(v || [null, null])}
                        />
                    </Col>
                    <Col>
                        <Button type='primary' onClick={handleSearch}>{t('organizer_common_search')}</Button>
                    </Col>
                </Row>

                <div className='bg-white rounded-2'>
                    <Table
                        dataSource={sessions}
                        columns={columns}
                        rowKey='_id'
                        loading={loading}
                        pagination={{
                            current: page,
                            pageSize: 20,
                            total: totalRecords,
                            onChange: p => { setPage(p); loadData(p); },
                            showTotal: total => `${total} ${t('support_eintraege')}`,
                        }}
                        scroll={{ x: 800 }}
                    />
                </div>
            </Flex>

            <Modal
                open={exportModalOpen}
                title={<Title level={4} type='success'>{t('support_export')}</Title>}
                footer={null}
                onCancel={() => setExportModalOpen(false)}
            >
                <Flex vertical gap={16}>
                    <Form layout='vertical'>
                        <Form.Item label={t('support_export_type')}>
                            <Space>
                                <Button
                                    type={exportType === 'billable' ? 'primary' : 'default'}
                                    onClick={() => setExportType('billable')}
                                >
                                    {t('support_export_billable')}
                                </Button>
                                <Button
                                    type={exportType === 'sheet' ? 'primary' : 'default'}
                                    onClick={() => setExportType('sheet')}
                                >
                                    {t('support_export_sheet')}
                                </Button>
                            </Space>
                        </Form.Item>
                        <Form.Item label={t('support_export_zeitraum')}>
                            <RangePicker
                                format='DD.MM.YYYY'
                                style={{ width: '100%' }}
                                value={exportRange}
                                onChange={v => setExportRange(v || [null, null])}
                            />
                        </Form.Item>
                    </Form>
                    <Row justify='end' gutter={8}>
                        <Col>
                            <Button onClick={() => setExportModalOpen(false)}>
                                {t('organizer_common_cancelButton')}
                            </Button>
                        </Col>
                        <Col>
                            <Button type='primary' icon={<DownloadOutlined />} loading={exportLoading} onClick={handleExport}>
                                {t('support_export_download')}
                            </Button>
                        </Col>
                    </Row>
                </Flex>
            </Modal>
        </Fragment>
    );
};

export default SupportVerlaufPage;
